import React from "react"
import CurrencyComponent from "components/Common/CurrencyComponent"
import { calculateAdjustedQuantity } from "utils/utils"

const nowrap = { whiteSpace: "nowrap" }

const receiptLineMetaColumns = () => [
  {
    key: "fund_code",
    label: "FUND CODE",
    render: row =>
      row?.fund_code?.code || row?.provider_in?.fund_code?.code || "-",
  },
  {
    key: "shipment",
    label: "SHIPMENT",
    render: row =>
      row?.shipment?.name || row?.provider_in?.shipment?.name || "-",
  },
  {
    key: "waybill_number",
    label: "WAYBILL NUMBER",
    render: row =>
      row?.waybill_number || row?.provider_in?.waybill_number || "-",
  },
  {
    key: "commodity_classification",
    label: "Unit Classification",
    render: row =>
      row?.commodity_classification?.name ||
      row?.provider_in?.commodity_classification?.name ||
      "-",
  },
]

const baseReceiptColumns = ({ includeReceiptNumber = true } = {}) => {
  const columns = [
    {
      key: "warehouse",
      label: "WAREHOUSE NAME",
      render: row => row?.warehouse?.name,
    },
  ]

  if (includeReceiptNumber) {
    columns.push({
      key: "receipt_number",
      label: "DS NUMBER",
      render: row =>
        row?.receipt_number || row?.provider_in?.receipt_number || "-",
    })
  }

  columns.push(
    {
      key: "transaction_date",
      label: "TRANSACTION DATE",
      render: row => row?.date,
    },
    {
      key: "transaction_type",
      label: "TRANSACTION TYPE",
      render: row => row?.type,
    }
  )

  return columns
}

const itemDetailColumns = () => [
  {
    key: "unfpa_item_id",
    label: "UNFPA ITEM ID",
    render: row => row?.item?.unfp_item_name,
  },
  {
    key: "item_description",
    label: "P- ITEM DESCRIPTION",
    render: row => row?.item?.ip_item_description,
  },
  {
    key: "unfpa_uom",
    label: "UNFPA UOM",
    render: row => (
      <>
        {row?.item?.uom}&nbsp;
        {(row?.unit_uom_quantity_per_unfpa ||
          row?.provider_in?.unit_uom_quantity_per_unfpa) > 1 &&
          (row?.unit_uom_quantity_per_unfpa ||
            row?.provider_in?.unit_uom_quantity_per_unfpa)}
      </>
    ),
  },
  {
    key: "ip_uom",
    label: "IP UOM",
    render: row => (
      <>
        {row?.item?.ip_unit_umo}&nbsp;
        {(row?.unit_umo_quantity_per_ip ||
          row?.provider_in?.unit_umo_quantity_per_ip) > 1 &&
          (row?.unit_umo_quantity_per_ip ||
            row?.provider_in?.unit_umo_quantity_per_ip)}
      </>
    ),
  },
  {
    key: "unit_price_unfpa",
    label: "UNIT PRICE (AS PER UNFPA)",
    render: row => (
      <CurrencyComponent
        currency={row?.currency || row?.provider_in?.currency}
        amount={
          row?.unit_price_per_unfpa || row?.provider_in?.unit_price_per_unfpa
        }
      />
    ),
  },
  {
    key: "unit_price_ip",
    label: "UNIT PRICE (AS PER IP UOM)",
    render: row => (
      <CurrencyComponent
        currency={row?.currency || row?.provider_in?.currency}
        amount={row?.unit_price_per_ip || row?.provider_in?.unit_price_per_ip}
      />
    ),
  },
  {
    key: "batch_number",
    label: "BATCH / LOT NUMBER",
    render: row => row?.batch_number || row?.provider_in?.batch_number,
  },
  {
    key: "expire_date",
    label: "EXPIRY / BEST BEFORE DATE",
    render: row => row?.expire_date || row?.provider_in?.expire_date,
  },
  {
    key: "qty_unfpa",
    label: "QUANTITY (UNFPA UOM)",
    render: row => row?.qty_per_unfpa,
  },
  {
    key: "qty_ip",
    label: "QUANTITY (IP UOM)",
    render: row => calculateAdjustedQuantity(row),
  },
  {
    key: "total_value",
    label: "VALUE ( AS PER UNFPA UOM)",
    render: row => (
      <CurrencyComponent currency={row?.currency} amount={row?.total_value} />
    ),
  },
]

const recipientColumns = () => [
  {
    key: "recipient_facility",
    label: "RECIPIENT FACILITY (WAREHOUSES, SDPS AND OTHER PARTNERS)",
    render: row => {
      const type = row?.type
      const warehouseName = row?.warehouse?.name
      const provinceName = row?.province?.province
      const facilityName = row?.facility?.facility_name
      const otherHfs = row?.other_hfs

      let value =
        type === "Receipts" ? `${warehouseName} WAREHOUSES` : provinceName

      if (facilityName && facilityName !== "(N/A)") {
        value += ` ${facilityName}`
      } else if (otherHfs) {
        value += ` ( ${otherHfs} )`
      }

      return value
    },
  },
  {
    key: "geographic_region",
    label: "GEOGRAPHIC REGION COVERED BY THE RECIPIENT",
    render: row =>
      row?.province
        ? `${row?.province?.province} - ${row?.province?.region || ""}`
        : "-",
  },
  {
    key: "donor",
    label: "DONAR",
    render: () => "UNFPA",
  },
]

export const REPORT_COLUMN_KEYS = {
  PROVIDER_RECEIPT: "providerReceipt",
  MONITOR_RECEIPT: "monitorReceipt",
  PROVIDER_TRANSACTION: "providerTransaction",
  MONITOR_TRANSACTION: "monitorTransaction",
  PROVIDER_TRANSACTION_BY_PROJECT: "providerTransactionByProject",
  PROVIDER_DISTRIBUTION: "providerDistribution",
  MONITOR_DISTRIBUTION: "monitorDistribution",
  PROVIDER_IP_DISTRIBUTION: "providerIpDistribution",
  MONITOR_IP_DISTRIBUTION: "monitorIpDistribution",
  PROVINCE_TRANSACTION: "provinceTransaction",
  PROVIDER_CONSUMPTION: "providerConsumption",
  MONITOR_CONSUMPTION: "monitorConsumption",
  PROVIDER_CONSUMPTION_TWO: "providerConsumptionTwo",
  MONITOR_CONSUMPTION_TWO: "monitorConsumptionTwo",
  PROVIDER_RECEIPT_HEALTH: "providerReceiptHealth",
  MONITOR_RECEIPT_HEALTH: "monitorReceiptHealth",
  PROVIDER_QUANTIFICATION: "providerQuantification",
  MONITOR_QUANTIFICATION: "monitorQuantification",
  PROVIDER_QUANTIFICATION_ORG: "providerQuantificationOrg",
  MONITOR_QUANTIFICATION_ORG: "monitorQuantificationOrg",
  PROVIDER_INVENTORY: "providerInventory",
  MONITOR_INVENTORY: "monitorInventory",
  PROVIDER_ME_RECEIPTS: "providerMeReceipts",
  PROVINCE_CONSUMPTION: "provinceConsumption",
}

const definitions = {
  [REPORT_COLUMN_KEYS.PROVIDER_RECEIPT]: () => [
    ...baseReceiptColumns(),
    ...itemDetailColumns(),
    ...receiptLineMetaColumns(),
  ],
  [REPORT_COLUMN_KEYS.MONITOR_RECEIPT]: () => [
    {
      key: "provider",
      label: "PROVIDER",
      render: row => row?.ProviderIp,
    },
    ...baseReceiptColumns({ includeReceiptNumber: false }),
    ...itemDetailColumns(),
    ...receiptLineMetaColumns(),
  ],
  [REPORT_COLUMN_KEYS.PROVIDER_TRANSACTION]: () => [
    ...baseReceiptColumns(),
    {
      key: "transaction_sub_type",
      label: "TRANSACTION SUB-TYPE",
      render: row => row?.transaction_sub_type,
    },
    ...itemDetailColumns().map(column =>
      column.key === "qty_ip"
        ? {
            ...column,
            render: row => calculateAdjustedQuantity(row),
          }
        : column.key === "unit_price_unfpa"
        ? {
            ...column,
            render: row => (
              <CurrencyComponent
                currency={row?.currency}
                amount={row?.provider_in?.unit_price_per_unfpa}
              />
            ),
          }
        : column.key === "unit_price_ip"
        ? {
            ...column,
            render: row => (
              <CurrencyComponent
                currency={row?.currency}
                amount={row?.provider_in?.unit_price_per_ip}
              />
            ),
          }
        : column
    ),
    ...receiptLineMetaColumns(),
    ...recipientColumns(),
  ],
  [REPORT_COLUMN_KEYS.MONITOR_TRANSACTION]: () =>
    definitions[REPORT_COLUMN_KEYS.PROVIDER_TRANSACTION](),
  [REPORT_COLUMN_KEYS.PROVIDER_TRANSACTION_BY_PROJECT]: () => [
    {
      key: "project",
      label: "PROJECT",
      render: row => row?.project?.name,
    },
    ...definitions[REPORT_COLUMN_KEYS.PROVIDER_TRANSACTION](),
  ],
  [REPORT_COLUMN_KEYS.PROVIDER_DISTRIBUTION]: () => [
    ...baseReceiptColumns(),
    {
      key: "distribution_type",
      label: "DISTRIBUTION TYPE",
      render: row => row?.distributed_type,
    },
    {
      key: "ip",
      label: "IP",
      render: row => (
        <>
          {row?.ip?.name}
          {![undefined, "", null].includes(row?.distributor) &&
            ` - ${row?.distributor}`}
        </>
      ),
    },
    ...itemDetailColumns().map(column =>
      column.key === "unit_price_unfpa" ||
      column.key === "unit_price_ip" ||
      column.key === "batch_number" ||
      column.key === "expire_date"
        ? {
            ...column,
            render: row => {
              if (column.key === "unit_price_unfpa") {
                return (
                  <CurrencyComponent
                    currency={row?.provider_in?.currency}
                    amount={row?.provider_in?.unit_price_per_unfpa}
                  />
                )
              }
              if (column.key === "unit_price_ip") {
                return (
                  <CurrencyComponent
                    currency={row?.provider_in?.currency}
                    amount={row?.provider_in?.unit_price_per_ip}
                  />
                )
              }
              if (column.key === "batch_number") {
                return row?.provider_in?.batch_number
              }
              if (column.key === "expire_date") {
                return row?.provider_in?.expire_date
              }
              return column.render(row)
            },
          }
        : column.key === "qty_ip"
        ? { ...column, render: row => row?.qty_per_ip }
        : column
    ),
    ...receiptLineMetaColumns(),
    ...recipientColumns(),
  ],
  [REPORT_COLUMN_KEYS.MONITOR_DISTRIBUTION]: () =>
    definitions[REPORT_COLUMN_KEYS.PROVIDER_DISTRIBUTION](),
  [REPORT_COLUMN_KEYS.PROVIDER_IP_DISTRIBUTION]: () => [
    {
      key: "transaction_date",
      label: "TRANSACTION DATE",
      render: row => row?.date,
    },
    {
      key: "transaction_type",
      label: "TRANSACTION TYPE",
      render: row => row?.type,
    },
    {
      key: "province",
      label: "PROVINCE",
      render: row => row?.province?.province,
    },
    {
      key: "facility",
      label: "FACILITY",
      render: row => row?.facility?.facility_name,
    },
    ...itemDetailColumns().slice(0, 4),
    {
      key: "batch_number",
      label: "BATCH / LOT NUMBER",
      render: row => row?.batch_number,
    },
    {
      key: "expire_date",
      label: "EXPIRY / BEST BEFORE DATE",
      render: row => row?.expire_date,
    },
    {
      key: "qty",
      label: "QUANTITY",
      render: row => row?.qty,
    },
  ],
  [REPORT_COLUMN_KEYS.MONITOR_IP_DISTRIBUTION]: () =>
    definitions[REPORT_COLUMN_KEYS.PROVIDER_IP_DISTRIBUTION](),
  [REPORT_COLUMN_KEYS.PROVINCE_TRANSACTION]: () => [
    {
      key: "provider",
      label: "PROVIDER",
      render: row => row?.provider?.title,
    },
    {
      key: "transaction_date",
      label: "TRANSACTION DATE",
      render: row => row?.date,
    },
    {
      key: "transaction_type",
      label: "TRANSACTION TYPE",
      render: row => row?.type,
    },
    ...itemDetailColumns().slice(0, 4),
    {
      key: "province",
      label: "PROVINCE",
      render: row => row?.province?.province,
    },
    {
      key: "facility",
      label: "FACILITY",
      render: row => row?.facility?.facility_name,
    },
    {
      key: "distributor",
      label: "DISTRIBUTOR",
      render: row => (row?.distributor !== "Receipts" ? row?.distributor : ""),
    },
    {
      key: "batch_number",
      label: "BATCH / LOT NUMBER",
      render: row => row?.province_in?.batch_number,
    },
    {
      key: "expire_date",
      label: "EXPIRY / BEST BEFORE DATE",
      render: row => row?.province_in?.expire_date,
    },
    {
      key: "qty",
      label: "QUANTITY",
      render: row => row?.qty,
    },
  ],
  [REPORT_COLUMN_KEYS.PROVIDER_CONSUMPTION]: () => [
    ...itemDetailColumns().slice(0, 4),
    { key: "ip", label: "IP", render: row => row?.ip_name },
    { key: "province", label: "PROVINCE", render: row => row?.province_name },
    {
      key: "facility",
      label: "FACILITY",
      render: row => row?.facility_name,
      visible: false,
    },
    { key: "receipt", label: "RECEIPT", render: row => row?.totalQty ?? 0 },
    {
      key: "consumption",
      label: "CONSUMPTION",
      render: row => row?.totalConsums ?? 0,
    },
    {
      key: "balance",
      label: "BALANCE",
      render: row => (row?.totalQty ?? 0) - (row?.totalConsums ?? 0),
    },
    {
      key: "percentage",
      label: "PERCENTAGE",
      render: row => row?.percentage,
    },
  ],
  [REPORT_COLUMN_KEYS.MONITOR_CONSUMPTION]: () =>
    definitions[REPORT_COLUMN_KEYS.PROVIDER_CONSUMPTION](),
  [REPORT_COLUMN_KEYS.PROVIDER_RECEIPT_HEALTH]: () => [
    ...itemDetailColumns().slice(0, 4),
    {
      key: "active",
      label: "ACTIVE",
      render: row => row?.totalActiveReceipt,
    },
    {
      key: "near_expire",
      label: "NEAR TO EXPIRE",
      render: row => row?.totalExpireReceipt,
    },
  ],
  [REPORT_COLUMN_KEYS.MONITOR_RECEIPT_HEALTH]: () =>
    definitions[REPORT_COLUMN_KEYS.PROVIDER_RECEIPT_HEALTH](),
  [REPORT_COLUMN_KEYS.PROVIDER_QUANTIFICATION]: () => [
    { key: "sn", label: "SN", render: (_, index) => index + 1 },
    { key: "item", label: "ITEM", render: row => row?.item_name },
    { key: "uom", label: "UOM", render: row => row?.uom },
    { key: "cost", label: "COST", render: row => row?.item_price },
    {
      key: "opening_balance",
      label: "OPENING BALANCE",
      render: row => row?.openingBalance,
    },
    { key: "received", label: "RECEIVED", render: row => row?.totalReceipts },
    {
      key: "distributed",
      label: "DISTRIBUTED",
      render: row => row?.totalDistribution,
    },
    { key: "balance", label: "BALANCE", render: row => row?.balance },
  ],
  [REPORT_COLUMN_KEYS.MONITOR_QUANTIFICATION]: () =>
    definitions[REPORT_COLUMN_KEYS.PROVIDER_QUANTIFICATION](),
  [REPORT_COLUMN_KEYS.PROVIDER_QUANTIFICATION_ORG]: () => [
    {
      key: "organization",
      label: "ORGANIZATION RECIEPT THE FP COMMODITES DURING THE YEAR",
      render: row => (row?.distributor ? row?.distributor : row?.ip?.name),
    },
    {
      key: "province",
      label: "PROVINCE",
      render: row => row?.province?.province,
    },
    {
      key: "total_facilities",
      label: "NO OF FACILITIES",
      render: row => row?.totalFacility,
    },
    {
      key: "submission_date",
      label: "SUBMISSION DATE",
      render: row => row?.date,
    },
  ],
  [REPORT_COLUMN_KEYS.MONITOR_QUANTIFICATION_ORG]: () =>
    definitions[REPORT_COLUMN_KEYS.PROVIDER_QUANTIFICATION_ORG](),
  [REPORT_COLUMN_KEYS.PROVIDER_CONSUMPTION_TWO]: () => [
    { key: "sn", label: "SN", render: (_, index) => index + 1 },
    { key: "ip", label: "IP", render: row => row?.ip?.name },
    {
      key: "province",
      label: "PROVINCE",
      render: row => row?.province?.province,
    },
    {
      key: "facility",
      label: "HEALTH FACILITY",
      render: row => row?.facility?.facility_name,
    },
    {
      key: "facility_type",
      label: "HEALTH FACILITY TYPE",
      render: row => row?.facility?.facility_type,
    },
    {
      key: "hmis_code",
      label: "HMIS CODE",
      render: row => row?.facility?.facility_code,
    },
    {
      key: "item_category",
      label: "ITEM CATEGORY",
      render: row => row?.item_category?.name,
    },
    {
      key: "item",
      label: "ITEM",
      render: row => row?.item?.ip_item_description,
    },
    {
      key: "opening_balance",
      label: "OPENING BALANCE",
      render: row => row?.totalQty,
    },
    {
      key: "jan",
      label: "JAN",
      render: row =>
        row?.ProviderTotalConsumsBasedOfMonth?.find(m => m.month === "01")
          ?.total ?? 0,
    },
    {
      key: "feb",
      label: "FEB",
      render: row =>
        row?.ProviderTotalConsumsBasedOfMonth?.find(m => m.month === "02")
          ?.total ?? 0,
    },
    {
      key: "mar",
      label: "MAR",
      render: row =>
        row?.ProviderTotalConsumsBasedOfMonth?.find(m => m.month === "03")
          ?.total ?? 0,
    },
    {
      key: "apr",
      label: "APR",
      render: row =>
        row?.ProviderTotalConsumsBasedOfMonth?.find(m => m.month === "04")
          ?.total ?? 0,
    },
    {
      key: "may",
      label: "MAY",
      render: row =>
        row?.ProviderTotalConsumsBasedOfMonth?.find(m => m.month === "05")
          ?.total ?? 0,
    },
    {
      key: "jun",
      label: "JUN",
      render: row =>
        row?.ProviderTotalConsumsBasedOfMonth?.find(m => m.month === "06")
          ?.total ?? 0,
    },
    {
      key: "jul",
      label: "JUL",
      render: row =>
        row?.ProviderTotalConsumsBasedOfMonth?.find(m => m.month === "07")
          ?.total ?? 0,
    },
    {
      key: "aug",
      label: "AUG",
      render: row =>
        row?.ProviderTotalConsumsBasedOfMonth?.find(m => m.month === "08")
          ?.total ?? 0,
    },
    {
      key: "sep",
      label: "SEP",
      render: row =>
        row?.ProviderTotalConsumsBasedOfMonth?.find(m => m.month === "09")
          ?.total ?? 0,
    },
    {
      key: "oct",
      label: "OCT",
      render: row =>
        row?.ProviderTotalConsumsBasedOfMonth?.find(m => m.month === "10")
          ?.total ?? 0,
    },
    {
      key: "nov",
      label: "NOV",
      render: row =>
        row?.ProviderTotalConsumsBasedOfMonth?.find(m => m.month === "11")
          ?.total ?? 0,
    },
    {
      key: "dec",
      label: "DEC",
      render: row =>
        row?.ProviderTotalConsumsBasedOfMonth?.find(m => m.month === "12")
          ?.total ?? 0,
    },
    {
      key: "total_consumption",
      label: "TOTAL CONSUMPTION",
      render: row => row?.ProviderTotalConsums ?? 0,
    },
    {
      key: "balance",
      label: "BALANCE",
      render: row => (row?.totalQty ?? 0) - (row?.ProviderTotalConsums ?? 0),
    },
  ],
  [REPORT_COLUMN_KEYS.MONITOR_CONSUMPTION_TWO]: () =>
    definitions[REPORT_COLUMN_KEYS.PROVIDER_CONSUMPTION_TWO](),
  [REPORT_COLUMN_KEYS.PROVIDER_INVENTORY]: () => [
    { key: "sn", label: "SN", render: (_, index) => index + 1 },
    {
      key: "item_description",
      label: "IP- ITEM DESCRIPTION",
      render: row => row?.item_name,
    },
    {
      key: "opening_balance",
      label: "OPENING BALANCE",
      render: row => row?.openingBalance,
    },
    {
      key: "lost",
      label: "LOST QUANTITY",
      render: row => row?.totalLostQuantity,
    },
    {
      key: "receipts",
      label: "RECEIPTS ( IN )",
      render: row =>
        Math.max(
          0,
          (row?.provider_ins_sum_first_in_qty_per_ip ?? 0) -
            (row?.other_source_receipt ?? 0)
        ),
    },
    {
      key: "other_source",
      label: "OTHER SOURCE RECEIPTS ( IN )",
      render: row => row?.other_source_receipt,
    },
    {
      key: "adjustment_increase",
      label: "ADJUSTMENT INCREASE",
      render: row => row?.adjustment_increase,
    },
    {
      key: "adjustment_decrease",
      label: "ADJUSTMENT DECREASE",
      render: row => row?.adjustment_decrease,
    },
    {
      key: "distribution",
      label: "DISTRIBUTION ( OUT )",
      render: row => row?.provider_outs_sum_out_quantity,
    },
    {
      key: "exchange",
      label: "EXCHANGE",
      render: row => row?.exchange_distribution,
    },
    { key: "amc", label: "AMC", render: row => row?.amc },
    { key: "balance", label: "BALANCE", render: row => row?.balance },
  ],
  [REPORT_COLUMN_KEYS.MONITOR_INVENTORY]: () =>
    definitions[REPORT_COLUMN_KEYS.PROVIDER_INVENTORY](),
  [REPORT_COLUMN_KEYS.PROVIDER_ME_RECEIPTS]: () => [
    { key: "sn", label: "S.NO", render: (_, index) => index + 1 },
    {
      key: "item",
      label: "ITEM ID - DESCRIPTION",
      render: row =>
        `${row?.item?.unfp_item_name} - ${row?.item?.ip_item_description}`,
    },
    {
      key: "provider",
      label: "PROVIDER",
      render: row => row?.provider?.title || "UNFPA",
    },
    {
      key: "unfpa_uom",
      label: "UNFPA UOM",
      render: row => (
        <>
          {row?.item?.uom}&nbsp;
          {row?.unit_uom_quantity_per_unfpa > 1 &&
            row?.unit_uom_quantity_per_unfpa}
        </>
      ),
    },
    {
      key: "ip_uom",
      label: "IP UOM",
      render: row => (
        <>
          {row?.item?.ip_unit_umo}&nbsp;
          {row?.unit_umo_quantity_per_ip > 1 && row?.unit_umo_quantity_per_ip}
        </>
      ),
    },
    {
      key: "unit_price_unfpa",
      label: "UNIT PRICE (UNFPA)",
      render: row => row?.unit_price_per_unfpa,
    },
    {
      key: "unit_price_ip",
      label: "UNIT PRICE (IP)",
      render: row => row?.unit_price_per_ip,
    },
    {
      key: "batch_number",
      label: "BATCH NUMBER",
      render: row => row?.batch_number,
    },
    {
      key: "expire_date",
      label: "EXPIRY DATE",
      render: row => row?.expire_date,
    },
    { key: "qty", label: "QUANTITY (IP)", render: row => row?.in_qty_per_ip },
    {
      key: "transaction_date",
      label: "DATE",
      render: row =>
        row?.date ? new Date(row.date).toLocaleDateString("en-GB") : "-",
    },
    ...receiptLineMetaColumns(),
  ],
  [REPORT_COLUMN_KEYS.PROVINCE_CONSUMPTION]: () =>
    definitions[REPORT_COLUMN_KEYS.PROVIDER_CONSUMPTION_TWO]().map(column => {
      if (column.key === "opening_balance") {
        return {
          ...column,
          render: row => row?.totalQtyForConsumptionReport,
        }
      }
      if (column.key === "total_consumption") {
        return { ...column, render: row => row?.totalConsums ?? 0 }
      }
      if (
        column.key.startsWith("jan") ||
        [
          "feb",
          "mar",
          "apr",
          "may",
          "jun",
          "jul",
          "aug",
          "sep",
          "oct",
          "nov",
          "dec",
        ].includes(column.key)
      ) {
        const monthMap = {
          jan: 1,
          feb: 2,
          mar: 3,
          apr: 4,
          may: 5,
          jun: 6,
          jul: 7,
          aug: 8,
          sep: 9,
          oct: 10,
          nov: 11,
          dec: 12,
        }
        return {
          ...column,
          render: row =>
            row?.totalConsumsBasedOfMonth?.find(
              m => Number(m.month) === monthMap[column.key]
            )?.total ?? 0,
        }
      }
      if (column.key === "balance") {
        return {
          ...column,
          render: row =>
            (row?.totalQtyForConsumptionReport ?? 0) - (row?.totalConsums ?? 0),
        }
      }
      return column
    }),
}

export const getReportColumnDefinitions = reportKey => {
  const builder = definitions[reportKey]

  if (!builder) {
    return []
  }

  return builder().map(column => ({
    visible: column.visible !== false,
    required: !!column.required,
    style: column.style || nowrap,
    ...column,
  }))
}
